import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { getBase64 } from '@repo/lib/fs/getBase64';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { FilePublic } from '@repo/types/files';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import { CheckCheck, Download, Info, LoaderCircle, UserIcon } from 'lucide-react';
import { useMemo } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { FileIcon } from '@/components/ui/file-icon';
import { FileCard, FileCardContent, FileCardTitle } from '@/components/ui/fs-card';
import { Image } from '@/components/ui/image';
import { AudioPlayer, VideoPlayer } from '@/components/ui/media-player';
import { getAvatar } from '@/utils/getAvatar';

interface MessageBubbleProps extends ComponentProps<'div'> {
  side: 'start' | 'end';
  variant: 'primary' | 'secondary';
}

export function MessageBubble(messageBubbleProps: MessageBubbleProps) {
  const { variant, side, className, ...props } = messageBubbleProps;
  return (
    <div
      data-slot="message-bubble"
      data-side={side}
      data-variant={variant}
      className={cn('group inline-flex w-fit flex-row gap-2 data-[side=end]:flex-row-reverse data-[side=end]:justify-start', className)}
      {...props}
    />
  );
}

export function MessageBubbleContainer(messageBubbleContainerProps: ComponentProps<'div'>) {
  const { className, ...props } = messageBubbleContainerProps;
  return <div data-slot="message-bubble-container" className={cn('w-fit space-y-1 overflow-hidden group-data-[side=end]:ms-12 group-data-[side=start]:me-12', className)} {...props} />;
}

export function MessageBubbleContent(messageBubbleContentProps: ComponentProps<'div'>) {
  const { className, ...props } = messageBubbleContentProps;
  return (
    <div
      data-slot="message-bubble-content"
      className={cn(
        'group-data-[side=start]:border-border-primary group-data-[variant=primary]:border-brand group-data-[variant=secondary]:bg-accent/50 dark:group-data-[variant=secondary]:bg-card group-data-[variant=primary]:bg-brand rounded-md border',
        className
      )}
      {...props}
    />
  );
}

export function MessageBubbleMessage(messageBubbleMessageProps: ComponentProps<'p'>) {
  const { className, ...props } = messageBubbleMessageProps;
  return (
    <p
      data-slot="message-bubble-message"
      className={cn(
        'group-data-[variant=secondary]:text-accent-foreground group-data-[variant=primary]:text-brand-foreground wrap-break-words w-full overflow-hidden p-2 text-sm whitespace-pre-line',
        className
      )}
      {...props}
    />
  );
}

export function MessageBubbleImage(messageBubbleImageProps: ComponentProps<typeof Image>) {
  const { className, loading = 'lazy', ...props } = messageBubbleImageProps;
  return (
    <div className={'relative p-1'}>
      <Image data-slot="message-bubble-image" className={cn('flex max-h-68 bg-card rounded-sm md:max-h-76 lg:max-h-86', className)} loading={loading} {...props} />
    </div>
  );
}

export function MessageBubbleVideo(messageBubbleVideoProps: ComponentProps<typeof VideoPlayer>) {
  const { className, ...props } = messageBubbleVideoProps;
  return (
    <div className={'w-full min-w-[70vw] p-1 sm:min-w-72'}>
      <VideoPlayer data-slot="message-bubble-video" autoHide={true} className={cn('max-h-52 bg-card rounded-sm md:max-h-62 lg:max-h-72', className)} {...props} />
    </div>
  );
}

export function MessageBubbleAudio(messageBubbleAudioProps: ComponentProps<typeof AudioPlayer>) {
  const { className, ...props } = messageBubbleAudioProps;
  return (
    <div className={'w-full min-w-[70vw] p-1 sm:min-w-72'}>
      <AudioPlayer data-slot="message-bubble-image" className={cn('bg-card rounded-sm p-2', className)} {...props} />
    </div>
  );
}

interface MessageBubbleFileProps extends ComponentProps<typeof FileCard> {
  name?: string;
  url?: string;
  className?: string;
}
export function MessageBubbleFile(messageBubbleFileProps: MessageBubbleFileProps) {
  const { className, name, url, ...props } = messageBubbleFileProps;
  return (
    <div className={'p-1'}>
      <FileCard data-slot="message-bubble-file-card" className={cn('bg-card rounded-sm', className)} {...props}>
        <FileCardContent>
          <FileIcon fileExtension={getFileExtension(name)} />
          <FileCardTitle>{name}</FileCardTitle>
          {url && (
            <Button variant={'ghost'} size={'sm'} asChild>
              <a href={url} download target="_self">
                <Download />
              </a>
            </Button>
          )}
        </FileCardContent>
      </FileCard>
    </div>
  );
}

interface MessageBubbleFilePDFProps extends ComponentProps<typeof FileCard> {
  name?: string;
  url?: string;
  className?: string;
  file?: FilePublic;
  onClick?: () => void;
}

export function MessageBubbleFilePDF(messageBubbleFilePDFProps: MessageBubbleFilePDFProps) {
  const { className, name, url, file, onClick, ...props } = messageBubbleFilePDFProps;
  const thumbnail = useMemo<string | undefined>(() => {
    if (!file?.thumbnail) return;

    return getBase64({
      fileType: file.fileType,
      base64: file.thumbnail,
      clear: true,
    });
  }, [file?.fileType, file?.thumbnail]);
  return (
    <div className={'space-y-1 p-1'}>
      <div className={cn('bg-card relative flex w-full justify-center rounded-sm p-1', !thumbnail && 'hidden')} onClick={onClick}>
        <Image className={'rounded-sm'} alt={`PDF - ${name}`} src={thumbnail ?? 'image'} />
      </div>
      <FileCard data-slot="message-bubble-file-card" className={cn('rounded-sm', className)} {...props}>
        <FileCardContent>
          <FileIcon fileExtension={getFileExtension(name)} />
          <FileCardTitle>{name}</FileCardTitle>
          {url && (
            <Button variant={'ghost'} size={'sm'} asChild>
              <a href={url} download target="_self">
                <Download />
              </a>
            </Button>
          )}
        </FileCardContent>
      </FileCard>
    </div>
  );
}

interface MessageBubbleReplyProps extends ComponentProps<'div'> {
  username?: string;
  content?: string;
}
export function MessageBubbleReply(messageBubbleReplyProps: MessageBubbleReplyProps) {
  const { className, username, content, children, ...props } = messageBubbleReplyProps;
  return (
    <div data-slot="message-bubble-reply" className={cn('bg-card mx-1 mt-1 rounded-sm', className)} {...props}>
      <div>{children}</div>
      <div className={'w-full space-y-1 p-2'}>
        <p className="text-brand truncate text-xs">{username}</p>
        <p className={'text-muted-foreground wrap-break-words line-clamp-10 text-sm empty:hidden'}>{content}</p>
      </div>
    </div>
  );
}

interface MessageBubbleAvatarProps extends ComponentProps<typeof Avatar> {
  avatarURL?: string;
}
export function MessageBubbleAvatar(messageBubbleAvatarProps: MessageBubbleAvatarProps) {
  const { avatarURL, className, ...props } = messageBubbleAvatarProps;
  return (
    <Avatar data-slot="message-bubble-avatar" size={'xs'} className={cn(className)} {...props}>
      <AvatarImage src={getAvatar(avatarURL)} />
      <AvatarFallback>
        <UserIcon />
      </AvatarFallback>
    </Avatar>
  );
}

interface MessageBubbleUsernameProps extends ComponentProps<'div'> {
  username: string;
}
export function MessageBubbleUsername(messageBubbleUsernameProps: MessageBubbleUsernameProps) {
  const { username, className, ...props } = messageBubbleUsernameProps;
  return (
    <div data-slot="message-bubble-username" className={cn(className)} {...props}>
      <p className={'text-muted-foreground/75 truncate text-xs whitespace-nowrap group-data-[side=end]:text-end group-data-[side=start]:text-start'}>{username}</p>
    </div>
  );
}

interface MessageBubbleFooterProps extends ComponentProps<'div'> {
  createdAt?: string;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}
export function MessageBubbleFooter(messageBubbleFooterProps: MessageBubbleFooterProps) {
  const { className, isPending, isSuccess, isError, createdAt, onRetry, ...props } = messageBubbleFooterProps;

  const icon = useMemo(() => {
    if (isPending) {
      return <LoaderCircle className={'group-data-[variant=secondary]:text-muted-foreground group-data-[variant=primary]:text-brand-foreground size-3.5 animate-spin'} />;
    } else if (isSuccess) {
      return <CheckCheck className={'group-data-[variant=secondary]:text-muted-foreground group-data-[variant=primary]:text-brand-foreground size-3.5'} />;
    } else if (isError) {
      return <Info className={'group-data-[variant=secondary]:text-destructive group-data-[variant=primary]:text-brand-foreground size-3.5'} />;
    }
  }, [isError, isPending, isSuccess]);

  return (
    <div data-slot="message-bubble-footer" className={cn('flex items-center justify-end gap-1 px-2 pb-2', className)} onClick={onRetry} {...props}>
      {icon}
      <p className={'group-data-[variant=secondary]:text-muted-foreground group-data-[variant=primary]:text-brand-foreground text-[11px] group-data-[side=end]:text-end'}>
        {createdAt ? formatTimeAgo(new Date(createdAt)) : ''}
      </p>
    </div>
  );
}

const formatTimeAgo = (date: Date): string => {
  if (isToday(date)) {
    return format(date, 'HH:mm');
  } else if (date > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) {
    return formatDistanceToNow(date, { addSuffix: true });
  } else {
    return format(date, 'MMM d, yyyy');
  }
};
